import { useAuth, useCachedFetch } from '@structural-climbing/auth'
import type { MyAddon, MySubscription } from './types'

const EMPTY: MyAddon[] = []

/** Prefix-invalidate with `invalidateCachedFetch('my-subscription:')`. */
export function mySubscriptionKey(gymId: string | null | undefined): string | null {
  return gymId ? `my-subscription:${gymId}` : null
}

/**
 * The member's own plan at one gym.
 *
 * This was a bare `fetch` in the dashboard's `useEffect`, which made it the one
 * member surface outside the shared cache — and so the one that never
 * revalidated when the app came back to the foreground. A plan cancelled at the
 * desk read "Active" until a hard reload.
 *
 * Through `useCachedFetch` it inherits foreground revalidation for free, which
 * is what keeps the card honest even when the realtime socket is dead.
 */
export function useMySubscription(gymId: string | null | undefined): {
  /** `undefined` while loading, `null` when the member has no plan here. */
  subscription: MySubscription | null | undefined
  /** Usable add-ons held next to the plan; empty while loading. */
  addons: MyAddon[]
  /** The renewal bought to follow the plan, if there is one. */
  upcoming: MySubscription | null
  refresh: () => Promise<void>
} {
  const { getAccessTokenSilently } = useAuth()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const { data, loading, refresh } = useCachedFetch<{
    subscription: MySubscription | null
    addons: MyAddon[]
    upcoming: MySubscription | null
  } | null>(mySubscriptionKey(gymId), async () => {
    const token = await getAccessTokenSilently()
    const res = await fetch(`${apiUrl}/my-subscription?gym_id=${encodeURIComponent(gymId!)}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    // A member with no plan is a 404-ish empty answer, not a failure: return
    // null so the hero renders its "no membership" state rather than a
    // permanent skeleton.
    if (!res.ok) return null
    const body = (await res.json()) as {
      subscription: MySubscription | null
      addons?: MyAddon[]
      upcoming_subscription?: MySubscription | null
    }
    return {
      subscription: body.subscription ?? null,
      addons: body.addons ?? [],
      upcoming: body.upcoming_subscription ?? null,
    }
  })

  return {
    subscription: loading ? undefined : (data?.subscription ?? null),
    addons: data?.addons ?? EMPTY,
    upcoming: data?.upcoming ?? null,
    refresh,
  }
}
